<template>
   <div class="priority-assessment">
      <process-header :step="6" />
      <div class="body">
         <div class="step-title">Evaluate and Prioritize the Activities</div>
         <div class='goal' v-for="goal in goals" :key="goal.id">
            <span class='goal-title'>Goal {{ goal.id }}: {{ goal.name }}</span>
            <div v-for='objective in goal.objectives' :key='objective.id' class='objective'>
               <div class='objective-head'>
                  <span class='objective-title'>Objective: {{ objective.description }}</span>
                  <span class='info-icon-wrapper'>
                     <info-icon color="secondary" size="small"
                        :content="prioritizationTips"
                        style="margin-top: -8px; cursor: pointer;"
                        v-tippy
                     />
                  </span>
               </div>
               <vue-editable-grid
                  :ref="`table${objective.id}`"
                  :id="`objective-${objective.id}`"
                  class='pa-table'
                  :column-defs="columnDefs"
                  :row-data="objective.assessments"
                  row-data-key="activity_id"
                  :enable-filters="false"
                  @cell-updated.prevent="event => cellUpdated(event, objective)"
                  :item-height="35"
               />
            </div>
         </div>
         <save-continue-btns
            :back-route="{name: 'activities'}"
            @save="save"
            @save-and-continue="saveAndContinue"
            not-for-form
         />
      </div>
   </div>
</template>

<script>
import ProcessHeader from "../Header";
import SaveContinueBtns from '@/components/molecules/Home/Plan/SaveContinueBtns';
import InfoIcon from "@/components/atoms/Home/InfoIcon";
import { fetchWithAuthToken, postWithAuthToken } from "../../../../../axios";
import { showError, showSuccess, showLoading, hideLoading, snakeToTitleCase } from "../../../../../js/ui-helpers";
import { isNumber, isString } from 'highcharts';

export default {
name: "priority-assesment",
components: {
   ProcessHeader,
   InfoIcon,
   SaveContinueBtns,
},
data()
{

   
   return {
      goals: [],
      assessmentFields: [],
      coloredFields: [],
      columnDefs: [],
   };
},
computed: {
   planId()
   {
      return this.$route.params.id;
   },
   prioritizationTips() {
      return `<div style="font-size: 0.95em;">
         <h4>How to prioritize activities</h4>
         <div>&bull; Prioritization score can range from 0 to 10, 0 being the lowest priority & 10 the highest.</div>
         <div>&bull; Enter the prioritization scores in the table based on the group discussion.</div>
         <div>&bull; The total prioritization score will be calculated from all criteria.</div>
         <div>&bull; The color of the cells will range from white to blue for highest to lowest values respectively for visual assistance.</div>
      </div>`;
      }
   },
   created()
   {
      // load column fields
      fetchWithAuthToken(`api/plan/${this.planId}/pf/`)
      .then(({data:factors}) =>
      {
         this.assessmentFields = factors.map(factor => factor.code);
         this.coloredFields = [...this.assessmentFields, 'total']
         this.columnDefs = this.getColumnDefs(factors);
      });

      const getAssessment = (activity) => ({
         ...activity.priority_assessment,
         activity_id: activity.id,
         activityDesc: activity.description,
         code: activity.code,
      });

      const formatObjective = ({id, description, activities}) =>
      {
         const assessments = this.getFormattedData(activities.map(getAssessment));
         return ({
            id,
            description,
            assessments,
         });
      };
      fetchWithAuthToken(`api/plan/${this.planId}/pa/`)
      .then(({data: goals}) =>
      {
         this.goals = goals.reduce((acc, goal) => [
            {
               ...goal,
               objectives: goal.objectives.map(formatObjective),
            },
            ...acc,
         ], []);
      });
  },
  methods: {
      getColumnDefs(factors)
      {
         const maxChars = 12;
         const getColumnWidth = field => `${(field.length < maxChars ? field.length : maxChars) + 2}ch`
         const assessmentColumns = factors.map(factor => ({
            field: factor.code,
            headerName: snakeToTitleCase(factor.name),
            sortable: true,
            type: 'numeric',
            min: 0,
            max: 10,
            size: getColumnWidth(factor.name),
            editable: true,
         }));
         const totalColumn = {
            field: "total",
            headerName: "Total",
            sortable: true,
            type: "numeric",
            min: 0,
            size: getColumnWidth('total'),
            max: assessmentColumns.length*10,
         };
         return [
            { field: "activityDesc", headerName: "Activity", sortable: true, size: '1fr', },
            { field: "code", headerName: "Code", sortable: true, size: '8ch', },
            ...assessmentColumns,
            totalColumn
         ];
      },
      getFormattedData(data)
      {
         return data.map((row) => this.getFormattedRow(row));
      },
      getColumnMinMax(field)
      {
         const column = this.columnDefs.find(col => col.field===field);
         console.assert(column, `Column not found for field: ${field}`);
         return column && {min: column.min, max: column.max};
      },
      getCellStyle(value, {min=0, max=10}={})
      {
         const color = "#7dacd5";
         const minOpacity = 10;
         const maxOpacity = 255; // actual max is 255
         const opacityRange = maxOpacity-minOpacity;
         const opacity = Math.ceil(value / (max-min) * opacityRange + minOpacity).toString(16);
         return { backgroundColor: `${color}${opacity.length==1 ? '0'+opacity : opacity }`};
      },
      getFormattedRow(row)
      {
         Object.keys(row)
         .filter(field => this.coloredFields.includes(field))
         .forEach(field => {
            row.$cellStyle = row.$cellStyle || {};
            row.$cellStyle[field] = this.getCellStyle(row[field], this.getColumnMinMax(field));
         });
         return row;
      },
      cellUpdated($event, objective)
      {
         const { column , row, value } = $event;
         const {min, max} = column;
         if ((isNumber(min) && value < min) || (isNumber(max) && value > max) || isString(value))
         {
            return showError(
               "Priority Assesment",
               `${column.headerName} must be a number between ${min} and ${max}.`
            );
         }
         row[column.field] = value;
         row.$cellStyle[column.field] = this.getCellStyle(value, {min, max});
         row.total = this.assessmentFields.reduce( ( total,field ) => total+row[field], 0);
         row.$cellStyle.total = this.getCellStyle(row.total, this.getColumnMinMax('total'));
         objective.isSaved = false;
      },
      getAssessmentFromRow(row)
      {
         const validFields = [...this.assessmentFields, 'activity_id'];
         return validFields.reduce((acc, field) => ({...acc, [field]: row[field]}), {});
      },
      getPostAssessmentData()
      {
         return this.goals.flatMap(goal => goal.objectives)
            .flatMap(objective => objective.assessments)
            .map(row => this.getAssessmentFromRow(row));
      },
      save({goToNext=false}={})
      {
         const data = this.getPostAssessmentData();
         showLoading()
         postWithAuthToken(`api/plan/${this.planId}/pa/`, data)
         .then(() =>
         {
            showSuccess('Priority Assessment', 'Successfully saved')
            if (goToNext)
            {
               this.$router.push({name: 'fundings'})
            }
         })
         .catch(() => showError('Priority Assessment', 'Failed to save data'))
         .finally(() => hideLoading());
      },
      saveAndContinue()
      {
         this.save({goToNext: true});
      }
   },
};
</script>

<style lang="scss" scoped>
.priority-assessment {
   font-family: $primary-font;
   color: $font-color;
   .step-title {
      font-family: $secondary-font;
      font-weight: 500;
      font-size: 1.05em;
      margin-bottom: 0.5em;
   }
   > .body {
      padding: 0.6em;
      .goal {
         background: $secondary-bg;
         padding: 0.5em;
         margin-top: 1em;
         border-radius: 5px;
         .goal-title {
            padding: 0.3em 0.2em;
            font-weight: 600;
            margin-bottom: 0.3em;
         }
         .objective {
            padding: 0.2em;
            &:not(:last-child) {
               margin-bottom: .7em;
            }
            border-radius: 5px;
            background: white;
            .objective-head {
               .objective-title {
                  font-weight: 600;
                  padding: 0.5em 0.2em;
               }
               display: flex;
               justify-content: space-between;
            }
         }
      }
   }
   .pa-table {
      min-height: 2em;
      font-size: 0.9em;
   }
   .info-icon-wrapper {
      display: flex;
      align-items: center;
      margin-right: 0.8em;
   }

   .btn-group {
      display: flex;
      margin: 0 0.3em;
      > * {
         margin-right: 0.3em;
         a {
         color: inherit;
         }
         &:last-child {
         margin-right: 0;
         }
      }
   }
}
</style>
